import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AddIcon from "../../../Assets/svgs/AddIcon";
import { LIMIT_PER_PAGE } from "../../../Common/Constants";
import { debounce } from "../../../Common/debounce";
import { Loader } from "../../../Common/Loader";
import { Pagination } from "../../../Common/Pagination";
import { Table } from "../../../Common/Table";
import useDebounce from "../../../Common/useDebounce";
import { ConfirmModel } from "../../../Model/ConfirmModel";
import { ErrorModel } from "../../../Model/ErrorModel";
import { SuccessModel } from "../../../Model/SuccessModel";
import {
  setPage,
  setTotalItems,
  setTotalPages,
} from "../../../Redux/Slice/Common/PaginationSlice";
import { deletePaymentMethod } from "../../../Redux/Slice/PaymentMethod/DeletePaymentMethodSlice";
import { getPaymentMethodList } from "../../../Redux/Slice/PaymentMethod/GetPaymentMethodListSlice";
import { makeDefaultPaymentMethod } from "../../../Redux/Slice/PaymentMethod/MakeDefaultPaymentMethodSlice";

const PaymentMethodsList = ({ setAddEditPaymentMethod }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const searchQuery = useDebounce(searchText, 500);
  const { paymentMethodListData } = useSelector((s) => s?.paymentMethods);
  const [localItemsPerPage, setLocalItemsPerPage] = useState(LIMIT_PER_PAGE);

  const [successModal, setSuccessModal] = useState({
    visible: false,
    message: "",
  });
  const [errorModal, setErrorModal] = useState({
    visible: false,
    message: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [confirmModel, setConfirmModel] = useState({
    visible: false,
    data: null,
  });

  const showErrorMessage = (message, callback) => {
    setErrorModal({
      visible: true,
      message: message ?? "Something went wrong, please try again later!",
    });

    setTimeout(() => {
      setErrorModal({
        visible: false,
        message: "",
      });
      if (typeof callback == "function") {
        callback();
      }
    }, 2000);
  };

  const showSuccessMessage = (message, callback) => {
    setSuccessModal({
      visible: true,
      message: message,
    });

    setTimeout(() => {
      setSuccessModal({
        visible: false,
        message: "",
      });
      if (typeof callback === "function") {
        callback();
      }
    }, 2000);
  };

  const onCancelClicked = () => {
    navigate(-1);
  };

  const handelCancel = () => {
    setConfirmModel(false);
  };

  const onAdd = () => {
    setAddEditPaymentMethod({
      isVisible: true,
      isEdit: false,
      data: null,
    });
  };

  const onEdit = (row) => {
    setAddEditPaymentMethod({
      isVisible: true,
      isEdit: true,
      data: row,
    });
  };

  const onDeleteClick = (row) => {
    setConfirmModel({
      visible: true,
      data: row,
    });
  };

  const formattedCardList = useMemo(() => {
    if (
      paymentMethodListData?.data &&
      Array.isArray(paymentMethodListData?.data)
    ) {
      return paymentMethodListData?.data.map((item) => {
        return {
          ...item,
          maskedCardNumber: `****-****-****-${item?.last4 ?? ""}`,
          cardExpiryDate: `${item?.expMonth}/${item?.expYear}`,
        };
      });
    }
    return [];
  }, [JSON.stringify(paymentMethodListData)]);

  const onMakeDefaultCard = async (id) => {
    setIsLoading(true);
    try {
      const result = await dispatch(makeDefaultPaymentMethod(id));
      setIsLoading(false);
      if (
        makeDefaultPaymentMethod.fulfilled.match(result) &&
        result?.payload?.success === true
      ) {
        showSuccessMessage(
          "Card has been successfully marked as default.",
          () => getCardsList(1, localItemsPerPage)
        );
      } else {
        showErrorMessage(result?.payload?.error?.message);
      }
    } catch (error) {
      setIsLoading(false);
      showErrorMessage(error?.message);
    }
  };

  const onDeleteCard = async () => {
    const cardId = confirmModel?.data?.id;
    setConfirmModel({
      visible: false,
      data: null,
    });
    setIsLoading(true);
    try {
      const result = await dispatch(deletePaymentMethod(cardId));
      setIsLoading(false);
      if (
        deletePaymentMethod.fulfilled.match(result) &&
        result?.payload?.success === true
      ) {
        showSuccessMessage("Card Deleted Successfully!", () =>
          getCardsList(1, localItemsPerPage)
        );
      } else {
        showErrorMessage(result?.payload?.error?.message);
      }
    } catch (error) {
      setIsLoading(false);
      showErrorMessage(error?.message);
    }
  };

  const getCardsList = async (page = 1, limit) => {
    setIsLoading(true);
    const result = await dispatch(
      getPaymentMethodList({ searchText, page, limit })
    );
    setIsLoading(false);
    dispatch(setPage(result?.payload?.pagination?.currentPage));
    dispatch(setTotalItems(result?.payload?.pagination?.totalItems));
    dispatch(setTotalPages(result?.payload?.pagination?.totalPages));
  };

  const PAYMENT_METHODS_TABLE_COLUMNS = [
    {
      label: "Card Number",
      key: "last4",
      className: "cellText_cardNumber",
    },
    {
      label: "Card Type",
      key: "brand",
      className: "cellText",
    },
    {
      label: "Expiry Date",
      key: "cardExpiryDate",
      className: "cellText",
    },
    {
      label: "Default Card",
      key: "isDefault",
      className: "switchBtn",
      onSwitchValueChanged: (val, rowData) => {
        if (val) {
          onMakeDefaultCard(rowData?.id);
        }
      },
    },
  ];

  const debouncedGetUserList = useCallback(debounce(getCardsList, 500), [
    searchQuery,
  ]);

  useEffect(() => {
    debouncedGetUserList(1, localItemsPerPage);
  }, [searchQuery, debouncedGetUserList]);

  return (
    <>
      <div className={"tabsContent__content tabsContent__content--selected"}>
        <div className="page__body">
          {isLoading ? <Loader /> : ""}
          {errorModal.visible ? (
            <ErrorModel message={errorModal.message} />
          ) : (
            ""
          )}
          {successModal.visible ? (
            <SuccessModel message={successModal.message} />
          ) : (
            ""
          )}
          {confirmModel.visible ? (
            <ConfirmModel
              onAccept={onDeleteCard}
              yes={"Yes"}
              no={"No"}
              onReject={handelCancel}
              title="Delete Credit Card!"
              message="Are you sure you want to delete this credit card?"
            />
          ) : (
            ""
          )}
          <div className="details h_100">
            <div className="form h_100">
              <section className="form__section">
                <div className="tableWrap" style={{ margin: 0, padding: 0 }}>
                  <div className="tableWrap__head">
                    <h1 className="form__section__title" style={{ margin: 0 }}>
                      Saved Credit Cards
                    </h1>
                    <div className="btn__group p_0">
                      <button className="btn btn__primary" onClick={onAdd}>
                        <AddIcon />
                        Add New Card
                      </button>
                    </div>
                  </div>
                  <Table
                    columns={PAYMENT_METHODS_TABLE_COLUMNS}
                    data={formattedCardList}
                    onDelete={onDeleteClick}
                    hasAction="true"
                    noDataMsg="No Saved Credit Cards!"
                  />
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
      <Pagination
        apiMethod={getCardsList}
        itemsPerPage={localItemsPerPage}
        setItemsPerPage={setLocalItemsPerPage}
      />
    </>
  );
};

export default PaymentMethodsList;
